import React from "react";
import { useHasBeenFalse } from "@hex-insights/core";
import {
	RadioInput,
	RadioInputProps,
	RadioOption,
	RadioOptionProps,
	TextInput,
	TextInputProps,
} from "@hex-insights/forms";

const valueOther = "__other__";

export type RadioTextInputProps = TextInputProps &
	Pick<
		RadioInputProps<string>,
		| "isLoading"
		| "options"
		| "displayOption"
		| "OptionsContainer"
		| "Option"
		| "optionClassName"
		| "selectedOptionClassName"
	>;

export function RadioTextInput({
	id,
	name,
	value,
	setValue,
	isLoading,
	options,
	onFocus,
	onBlur,
	type,
	inputMode,
	placeholder,
	autoComplete,
	autoFocus,
	disabled,
	allowLeadingWhitespace,
	allowTrailingWhitespace,
	Option = RadioOption,
	OptionsContainer,
	displayOption,
	optionClassName,
	selectedOptionClassName,
	className,
	style,
}: RadioTextInputProps) {
	const fullOptions = React.useMemo(() => {
		return [...options, { value: valueOther, label: "Other" }];
	}, [options]);

	const [isOtherSelected, setIsOtherSelected] = React.useState(() => {
		return !options.some((option) => option.value === value) && value !== "";
	});
	const hasBeenNotSelected = useHasBeenFalse(isOtherSelected);

	const setValueFromRadio = React.useCallback(
		(value: string) => {
			if (value === valueOther) {
				setIsOtherSelected(true);
				setValue("");
				return;
			}
			setIsOtherSelected(false);
			setValue(value);
		},
		[setValue],
	);

	const WrappedOption = React.useCallback(
		(props: RadioOptionProps<string>) => {
			if (props.value === valueOther) {
				if (isOtherSelected) {
					// use top-level setValue instead of that from props to avoid using setValueFromRadio
					return (
						<TextInput
							value={props.inputDetails.value}
							setValue={setValue}
							autoFocus={hasBeenNotSelected}
							placeholder={placeholder ?? "Other"}
							type={type}
							inputMode={inputMode}
							autoComplete={autoComplete}
							allowLeadingWhitespace={allowLeadingWhitespace}
							allowTrailingWhitespace={allowTrailingWhitespace}
							className="text-field__text-input__OmVfyhqJ"
						/>
					);
				}
				return <Option {...props} />;
			}
			return <Option {...props} />;
		},
		[
			Option,
			isOtherSelected,
			setValue,
			hasBeenNotSelected,
			placeholder,
			type,
			inputMode,
			autoComplete,
			allowLeadingWhitespace,
			allowTrailingWhitespace,
		],
	);

	return (
		<RadioInput
			id={id ?? name}
			name={name as string}
			value={value}
			options={fullOptions}
			isLoading={isLoading}
			setValue={setValueFromRadio}
			onFocus={onFocus}
			onBlur={onBlur}
			autoFocus={autoFocus}
			disabled={disabled}
			Option={WrappedOption}
			OptionsContainer={OptionsContainer}
			displayOption={displayOption}
			optionClassName={optionClassName}
			selectedOptionClassName={selectedOptionClassName}
			className={className}
			style={style}
		/>
	);
}
